import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ChatOsBotoes } from "@/components/ChatOsBotoes";

const TIPOS = ["montagem", "revisao", "assistencia", "transferencia", "desmontagem"];
const STATUS_TIPS = {
  aguardando_distribuicao: "Distribuindo",
  aguardando_aceite_tecnico: "Aguard. técnico",
  aguardando_disponibilidade: "Aguard. disponibilidade",
  aguardando_disponibilidade_montadora: "Aguard. montadora",
  aguardando_datas_tecnico: "Técnico p/ datas",
  aguardando_confirmacao_cliente: "Aguard. cliente",
  agendado: "Agendado",
  em_atendimento: "Em atendimento",
  finalizado: "Finalizado",
  aguardando_ordem: "Aguard. ordem",
};

const ETAPAS = {
  aguardando_distribuicao: { l: "Distribuindo", c: "bg-[#F4F4F5]" },
  aguardando_aceite_tecnico: { l: "Notificado · aguard. aceite", c: "bg-[#FFCC00]" },
  aguardando_disponibilidade: { l: "Aguardando disponibilidade de montador", c: "bg-[#FEE2E2] text-[#B91C1C]" },
  aguardando_disponibilidade_montadora: { l: "Aguardando disponibilidade de montadora", c: "bg-[#FEE2E2] text-[#B91C1C]" },
  aguardando_data_tecnico: { l: "Aceito · definindo data", c: "bg-[#DBEAFE] text-[#142766]" },
  aguardando_datas_tecnico: { l: "Aceito · definindo data", c: "bg-[#DBEAFE] text-[#142766]" },
  aguardando_confirmacao_cliente: { l: "Aguardando cliente", c: "bg-[#FEF3C7]" },
  agendado: { l: "Agendado", c: "bg-[#142766] text-white" },
  em_atendimento: { l: "Em atendimento", c: "bg-[#00A859] text-white" },
  finalizado: { l: "Concluído", c: "bg-[#00A859] text-white" },
  aguardando_ordem: { l: "Aguardando ordem", c: "bg-[#F4F4F5]" },
  ausente: { l: "Cliente ausente", c: "bg-[#E60000] text-white" },
  impedido: { l: "Impedido · ver ocorrência", c: "bg-[#E60000] text-white" },
};

export default function Servicos() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [naoLidas, setNaoLidas] = useState({});
  const [indic, setIndic] = useState(null);
  const [chip, setChip] = useState("");
  const [skip, setSkip] = useState(0);
  const [fimLista, setFimLista] = useState(true);
  const [fotosDe, setFotosDe] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const LIMIT = 200;
  const hojeISO = new Date().toISOString().slice(0, 10);
  const [open, setOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [busca, setBusca] = useState("");
  const [form, setForm] = useState(emptyForm());

  const load = (reset = true) => {
    const q = new URLSearchParams();
    if (filterStatus) q.append("status", filterStatus);
    if (filterTipo) q.append("tipo", filterTipo);
    if (chip) q.append("chip", chip);
    const sk = reset ? 0 : skip + LIMIT;
    q.append("skip", sk); q.append("limit", LIMIT);
    api.get(`/servicos?${q.toString()}`).then(r => {
      setList(l => (reset ? r.data : [...l, ...r.data]));
      setSkip(sk); setFimLista(r.data.length < LIMIT);
    });
  };

  const loadIndic = () => api.get("/servicos/indicadores").then(r => setIndic(r.data)).catch(() => {});
  useEffect(() => {
    loadIndic();
    const t = setInterval(loadIndic, 60000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportarExcel = async () => {
    try {
      const q = new URLSearchParams();
      if (filterStatus) q.append("status", filterStatus);
      if (filterTipo) q.append("tipo", filterTipo);
      if (chip) q.append("chip", chip);
      if (busca.trim()) q.append("busca", busca.trim());
      const r = await api.get(`/servicos/exportar.xlsx?${q.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `servicos_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Planilha exportada");
    } catch { toast.error("Não foi possível exportar a planilha"); }
  };

  const baixarLaudo = async (s) => {
    try {
      const r = await api.get(`/servicos/${s.id}/laudo-ocorrencia.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `laudo_${s.pim_code}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Não foi possível baixar o laudo"); }
  };

  const reenviarLink = async (s) => {
    try {
      await api.post(`/servicos/${s.id}/reenviar-link`);
      toast.success("Link reenviado ao cliente por WhatsApp");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [filterStatus, filterTipo, chip]);
  useEffect(() => { api.get("/empresas").then(r => setEmpresas(r.data)); }, []);

  const loadNaoLidas = () => {
    const ids = list.slice(0, 100).map(s => s.id).join(",");
    if (!ids) return;
    api.get("/chat/nao-lidas", { params: { ids } }).then(r => setNaoLidas(r.data)).catch(() => {});
  };
  useEffect(() => {
    loadNaoLidas();
    const t = setInterval(loadNaoLidas, 30000);
    return () => clearInterval(t);
  }, [list]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    try {
      await api.post("/servicos", {
        ...form,
        quantidade: Number(form.quantidade || 1),
        valor_recebido_empresa: form.valor_recebido_empresa ? Number(form.valor_recebido_empresa) : null,
      });
      toast.success("Serviço criado e distribuído");
      setOpen(false); setForm(emptyForm()); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateFlag = async (sid, k, v) => {
    const fd = new FormData();
    fd.append(k, String(v));
    await api.post(`/servicos/${sid}/flags`, fd);
    load();
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="label-cap">Operacional</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Serviços</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-sharp brand-bg text-white hover:bg-[#001A5E]" data-testid="btn-novo-servico">+ Novo serviço</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Novo serviço</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="label-cap">Tipo</Label>
                  <Select value={form.tipo} onValueChange={(v) => setForm(f => ({...f, tipo: v}))}>
                    <SelectTrigger className="mt-2" data-testid="srv-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIPOS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-cap">Empresa</Label>
                  <Select value={form.empresa_id} onValueChange={(v) => setForm(f => ({...f, empresa_id: v}))}>
                    <SelectTrigger className="mt-2" data-testid="srv-empresa"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{empresas.map(e => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <FF label="Nome do cliente" v={form.cliente_nome} on={(v) => setForm(f => ({...f, cliente_nome: v}))} testid="srv-cli-nome" />
              <div className="grid md:grid-cols-2 gap-4">
                <FF label="CPF" v={form.cliente_cpf} on={(v) => setForm(f => ({...f, cliente_cpf: v}))} testid="srv-cli-cpf" />
                <FF label="Telefone" v={form.cliente_telefone} on={(v) => setForm(f => ({...f, cliente_telefone: v}))} testid="srv-cli-tel" />
              </div>
              <FF label="Endereço" v={form.cliente_endereco} on={(v) => setForm(f => ({...f, cliente_endereco: v}))} testid="srv-cli-end" />
              <div className="grid md:grid-cols-2 gap-4">
                <FF label="Cidade" v={form.cliente_cidade} on={(v) => setForm(f => ({...f, cliente_cidade: v}))} testid="srv-cli-cidade" />
                <FF label="UF" v={form.cliente_estado} on={(v) => setForm(f => ({...f, cliente_estado: v}))} testid="srv-cli-uf" />
              </div>
              <FF label="Produto" v={form.produto} on={(v) => setForm(f => ({...f, produto: v}))} testid="srv-prod" />
              <div className="grid md:grid-cols-3 gap-4">
                <FF label="Quantidade" v={form.quantidade} on={(v) => setForm(f => ({...f, quantidade: v}))} testid="srv-qtd" />
                <FF label="Cor" v={form.cor} on={(v) => setForm(f => ({...f, cor: v}))} testid="srv-cor" />
                <FF label="Valor recebido R$" v={form.valor_recebido_empresa} on={(v) => setForm(f => ({...f, valor_recebido_empresa: v}))} testid="srv-valor" />
              </div>
              <Button onClick={submit} className="w-full btn-sharp brand-bg text-white" data-testid="srv-salvar">
                Criar e distribuir
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="torre-controle">
        {[
          ["atrasados", "Atrasados", indic?.atrasados, "border-l-[#E60000] text-[#B91C1C]"],
          ["mensagens", "Novas Mensagens", indic?.novas_mensagens, "border-l-[#2563EB] text-[#1D4ED8]"],
          ["assistencias", "Assistências em Aberto", indic?.assistencias_abertas, "border-l-[#F97316] text-[#C2410C]"],
          ["hoje", "Agendados p/ Hoje", indic?.agendados_hoje, "border-l-[#00A859] text-[#166534]"],
        ].map(([k, label, val, cor]) => (
          <button key={k} onClick={() => setChip(chip === k ? "" : k)}
            className={`text-left border border-border border-l-4 ${cor} bg-white p-3 hover:bg-secondary transition-colors ${chip === k ? "ring-2 ring-[#142766]" : ""}`}
            data-testid={`card-${k}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold">{label}</div>
            <div className="text-2xl font-extrabold">{val ?? "…"}</div>
            {k === "assistencias" && indic?.assistencias_d7 > 0 && (
              <div className="text-[10px] font-bold text-[#E60000]">{indic.assistencias_d7} com ≤7 dias (CDC)</div>
            )}
          </button>
        ))}
      </div>
      <div className="flex gap-2 mb-4 flex-wrap" data-testid="chips-filtros">
        {[["", "Todos"], ["concluidos", "Concluídos"], ["assistencias", "Assistências"], ["trocas", "Trocas"],
          ["aguardando_ordem", "Aguardando Ordem"], ["mensagens", "Mensagens Pendentes"]].map(([k, l]) => (
          <button key={k || "todos"} onClick={() => setChip(k)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${chip === k ? "bg-[#142766] text-white border-transparent" : "border-border bg-white hover:bg-secondary"}`}
            data-testid={`chip-${k || "todos"}`}>{l}</button>
        ))}
      </div>

      <div className="flex gap-3 mb-6 flex-wrap">
        <Input value={busca} onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por nome do cliente, OS ou código…"
          className="w-full sm:w-72 bg-white" data-testid="filter-busca" />
        <Select value={filterStatus || "todos"} onValueChange={(v) => setFilterStatus(v === "todos" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-56" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos status</SelectItem>
            {Object.keys(STATUS_TIPS).map(s => <SelectItem key={s} value={s}>{STATUS_TIPS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterTipo || "todos"} onValueChange={(v) => setFilterTipo(v === "todos" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-56" data-testid="filter-tipo"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos tipos</SelectItem>
            {TIPOS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={exportarExcel} className="btn-sharp border-[#00A859] text-[#166534] hover:bg-[#DCFCE7]" data-testid="exportar-excel-btn">
          ⬇ Exportar Excel
        </Button>
      </div>

      <div className="border border-border bg-white overflow-x-auto" data-testid="servicos-tabela-scroll">
        <table className="w-full min-w-[1100px] text-sm">
          <thead className="border-b border-border bg-[#F4F4F5]">
            <tr><Th>MontaJá</Th><Th>Tipo</Th><Th>Cliente</Th><Th>Tipo de Cliente</Th><Th>Montador</Th><Th>Produto</Th><Th>Cidade</Th><Th>Etapa</Th><Th>Chats</Th><Th>Flags</Th><Th></Th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={11} className="p-8 text-center text-muted-foreground">Nenhum serviço encontrado</td></tr>}
            {list.filter((s) => {
              const q = busca.trim().toLowerCase();
              if (!q) return true;
              return [s.cliente_nome, s.os_numero, s.pim_code, s.codigo_produto, s.produto]
                .some(v => String(v || "").toLowerCase().includes(q));
            }).map((s) => (
              <tr key={s.id} className={`border-b border-border last:border-0 hover:bg-secondary ${s.assistencia_dias_restantes != null && s.assistencia_dias_restantes <= 7 ? "bg-[#FFEDD5] border-l-4 border-l-[#F97316]" : ""}`} data-testid={`servico-row-${s.id}`}>
                <Td className="font-mono text-xs">{s.pim_code}</Td>
                <Td><span className="px-2 py-1 bg-secondary text-xs uppercase tracking-wider">{s.tipo}</span></Td>
                <Td>
                  <div className="flex items-center gap-2">
                    {s.urgente_juridico && <span className="urgent-pulse w-2 h-2 rounded-full bg-[#E60000]" title="Urgente jurídico" />}
                    <strong>{s.cliente_nome}</strong>
                  </div>
                  <div className="text-xs text-muted-foreground">{s.cliente_telefone}</div>
                </Td>
                <Td>
                  <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider inline-block max-w-[130px] truncate align-middle ${s.tipo_cliente === "Particular" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#E0E7FF] text-[#142766]"}`} title={s.tipo_cliente || ""} data-testid={`tipo-cliente-${s.id}`}>
                    {s.tipo_cliente || "—"}
                  </span>
                </Td>
                <Td data-testid={`montador-${s.id}`}>{s.montador_nome_curto || "—"}</Td>
                <Td>{s.produto} ({s.quantidade})</Td>
                <Td>{s.cliente_cidade}/{s.cliente_estado}</Td>
                <Td>
                  <span className={`px-2 py-1 text-[10px] uppercase tracking-wider font-bold whitespace-nowrap ${(ETAPAS[s.status] || { c: "bg-[#F4F4F5]" }).c}`}
                    data-testid={`etapa-${s.id}`}>
                    {(ETAPAS[s.status] || { l: s.status }).l}
                  </span>
                  {s.assistencia_dias_restantes != null && (
                    <div className={`mt-1 text-[10px] font-bold px-1.5 py-0.5 inline-block ${s.assistencia_dias_restantes <= 7 ? "bg-[#F97316] text-white" : "bg-[#FFEDD5] text-[#C2410C]"}`} data-testid={`cdc-${s.id}`}>
                      ⏳ CDC: {s.assistencia_dias_restantes} dia{s.assistencia_dias_restantes === 1 ? "" : "s"} restantes
                    </div>
                  )}
                  {s.reofertas > 0 && <div className="text-[10px] text-muted-foreground mt-1">reofertado {s.reofertas}x</div>}
                </Td>
                <Td>
                  <ChatOsBotoes servico={s} meuTipo={user?.role} naoLidas={naoLidas[s.id]} onLido={loadNaoLidas} />
                </Td>
                <Td>
                  <div className="flex gap-1">
                    <FlagBtn active={s.urgente_juridico} onClick={() => updateFlag(s.id, "urgente_juridico", !s.urgente_juridico)} color="#E60000" title="Jurídico urgente" letter="J" testid={`flag-juridico-${s.id}`} />
                    <FlagBtn active={s.cobranca_tecnico_manual} onClick={() => updateFlag(s.id, "cobranca_tecnico_manual", !s.cobranca_tecnico_manual)} color="#FFCC00" title="Cobrar técnico" letter="T" testid={`flag-tec-${s.id}`} />
                    <FlagBtn active={s.cobranca_cliente_manual} onClick={() => updateFlag(s.id, "cobranca_cliente_manual", !s.cobranca_cliente_manual)} color="#00A859" title="Cobrar cliente" letter="C" testid={`flag-cli-${s.id}`} />
                  </div>
                </Td>
                <Td className="min-w-[190px]">
                  <div className="flex items-center gap-1.5 flex-nowrap">
                    <Link to={`/admin/servicos/${s.id}`}>
                      <Button size="sm" variant="outline" className="btn-sharp h-8 text-xs" data-testid={`servico-ver-${s.id}`}>Ver</Button>
                    </Link>
                    {s.tem_ocorrencia && (
                      <button onClick={() => baixarLaudo(s)} title="Baixar Laudo de Ocorrência (PDF)"
                        className="w-8 h-8 border border-border bg-white flex items-center justify-center hover:bg-secondary text-[#B91C1C]"
                        data-testid={`laudo-btn-${s.id}`}>📄</button>
                    )}
                    <button onClick={() => setFotosDe(s)} title="Ver fotos"
                      className="w-8 h-8 border border-border bg-white flex items-center justify-center hover:bg-secondary"
                      data-testid={`fotos-btn-${s.id}`}>🖼</button>
                    {["aguardando_confirmacao_cliente", "aguardando_datas_tecnico", "aguardando_data_tecnico"].includes(s.status) && s.cliente_telefone && (
                      <button onClick={() => reenviarLink(s)} title="Reenviar link de agendamento (WhatsApp)"
                        className="w-8 h-8 border border-border bg-white flex items-center justify-center hover:bg-secondary text-[#00A859]"
                        data-testid={`reenviar-btn-${s.id}`}>📲</button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!fimLista && (
        <div className="mt-4 text-center">
          <Button variant="outline" className="btn-sharp" onClick={() => load(false)} data-testid="carregar-mais">
            Carregar mais serviços
          </Button>
        </div>
      )}
      <Dialog open={!!fotosDe} onOpenChange={(o) => !o && setFotosDe(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="fotos-dialog">
          <DialogHeader><DialogTitle>Fotos — {fotosDe?.pim_code} · {fotosDe?.cliente_nome}</DialogTitle></DialogHeader>
          {fotosDe && <FotosServico servicoId={fotosDe.id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FotosServico({ servicoId }) {
  const [evs, setEvs] = useState(null);
  useEffect(() => {
    api.get(`/servicos/${servicoId}`).then(r => setEvs((r.data.evidencias || []).filter(e => e.content_type?.startsWith("image/"))));
  }, [servicoId]);
  if (evs === null) return <div className="p-6 text-center text-muted-foreground text-sm">Carregando…</div>;
  if (!evs.length) return <div className="p-6 text-center text-muted-foreground text-sm" data-testid="fotos-vazio">Nenhuma foto enviada para esta OS.</div>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="fotos-grid">
      {evs.map(ev => (
        <div key={ev.id} className="border border-border p-1.5 text-[10px]">
          <div className="label-cap">{ev.categoria}</div>
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${ev.servico_id}/${ev.categoria}/${(ev.path || "").split("/").pop()}?s=${ev.sig || ""}`}
            alt={ev.filename} className="mt-1 aspect-square object-cover w-full" />
        </div>
      ))}
    </div>
  );
}

function emptyForm() {
  return { tipo: "montagem", empresa_id: "", cliente_nome: "", cliente_cpf: "", cliente_telefone: "",
    cliente_endereco: "", cliente_cidade: "", cliente_estado: "", produto: "", quantidade: "1", cor: "", valor_recebido_empresa: "" };
}
function FF({ label, v, on, testid }) {
  return <div><Label className="label-cap">{label}</Label><Input value={v||""} onChange={(e) => on(e.target.value)} className="mt-2" data-testid={testid} /></div>;
}
function FlagBtn({ active, onClick, color, title, letter, testid }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
      className="w-7 h-7 border text-xs font-bold flex items-center justify-center transition-colors"
      style={{ background: active ? color : "white", color: active ? "white" : "#71717A", borderColor: active ? color : "#E4E4E7" }}>
      {letter}
    </button>
  );
}
function Th({ children }) { return <th className="text-left p-3 label-cap font-medium whitespace-nowrap">{children}</th>; }
function Td({ children, className="" }) { return <td className={`p-3 ${className}`}>{children}</td>; }
